import gql from 'graphql-tag';
import Util from './Util';
import { Settings } from '@mui/icons-material';

const IP_LOCATION = gql`
query {
  ipLocation {
    label
    country
    adminDistrict
    adminDistrict2
    locality
    latitude
    longitude
  }
}
`;

const POINT_LOCATION = gql`
query pointLocation($point: Point) {
  pointLocation(point: $point) {
    label
    country
    adminDistrict
    adminDistrict2
    locality
    latitude
    longitude
  }
}
`;

const USER_LOCATION_COOKIE = "USER_LOCATION"

//GPS timeout in ms
const GPS_TIMEOUT = 60000;

class Location {
    static setUserLocation(location) {
        const jsonLocation = JSON.stringify(location)
        const b64Location = btoa(jsonLocation)
        Util.setCookie(USER_LOCATION_COOKIE, b64Location, 60 * 60);
    }

    static getUserLocation() {
        const b64Location = Util.getCookie(USER_LOCATION_COOKIE)
        if (!b64Location) {
            return null
        }
        const jsonLocation = atob(b64Location)
        return JSON.parse(jsonLocation)
    }

    static getLocator(sendLocator, options) {
        this.getGPSLocation(
            location => {
                sendLocator({coordinates: {latitude: location.coords.latitude, longitude: location.coords.longitude}})
            },
            error => {
                sendLocator({useRequestIpAddress: true})
            },
            options
        )
    }

    static getGPSLocation(success, error, options) {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                position => {
                    var coords = position?.coords;
                    if (coords.latitude && coords.longitude) {
                        success?.(position)
                    } else {
                        error?.("Unable to get coordinates")
                    }
                },
                err => error?.(err.message),
                {
                    enableHighAccuracy: options?.enableHighAccuracy || false,
                    timeout: GPS_TIMEOUT,
                    maximumAge: 0
                }
            )
        } else {
            error?.("Geolocation is not supported by this browser.")
        }
    }

    static getLocation(client, success, onError) {
        //First try to use GPS
        Location.getGPSLocation(
            position => {
                client.query({
                    query: POINT_LOCATION,
                    variables: { point: { latitude: position.coords.latitude, longitude: position.coords.longitude } }
                }).then(data => {
                    if (data.data.pointLocation === null || data.data.pointLocation.label === null) {
                        onError?.({ message: 'Unable to determine location' });
                    } else {
                        success(data.data.pointLocation);
                    }
                })
            },
            error => {
                console.log(error)
                Location.getIpLocation(client, success, onError);
            }
        )
    }

    static getIpLocation(client, success, onError) {
        //TODO IF timeout, retry
        client.query({
            query: IP_LOCATION
        }).then(data => {
            if (data.data.ipLocation === null || data.data.ipLocation.label === null) {
                onError?.({ message: 'Unable to determine location' });
            } else {
                success(data.data.ipLocation);
            }
        })
    }

    /**
     * Converts a location object into a URL-encoded string in the format "/local?name=City%2C+State+Country".
     * It skips locality and adminDistrict if they are not set.
     *
     * @param {object} locationObject - The object containing location details.
     * @param {string} [locationObject.locality] - The city name (e.g., "Columbia"). Optional.
     * @param {string} [locationObject.adminDistrict] - The administrative district/state (e.g., "Maryland"). Optional.
     * @param {string} locationObject.country - The country name (e.g., "United States"). Required.
     * @returns {string} The formatted and URL-encoded string.
     */
    static createLocalUrl(locationObject) {
        const name = Location.name(locationObject)

        // URL-encode the name string
        // encodeURIComponent is used to properly encode characters like spaces, commas, etc.
        const encodedName = encodeURIComponent(name);

        // Construct the final URL string
        const url = `/local?name=${encodedName}`;

        return url;
    }

    static name(locationObject) {
        if (locationObject?.label) {
            return locationObject?.label;
        }

        const parts = [];

        // Add locality if it exists
        if (locationObject.locality) {
            parts.push(locationObject.locality);
        }

        // Add adminDistrict if it exists.
        // If both locality and adminDistrict exist, add a comma before adminDistrict.
        if (locationObject.adminDistrict) {
            if (locationObject.locality) { // If locality is present, add a comma and space
            parts.push(`, ${locationObject.adminDistrict}`);
            } else { // If only adminDistrict is present, just add it
            parts.push(locationObject.adminDistrict);
            }
        }

        // Add country. If other parts exist, add a space before country.
        if (locationObject.country) {
            if (parts.length > 0) {
            // If there are previous parts, check if the last part already ended with a comma
            const lastPart = parts[parts.length - 1];
            if (lastPart.endsWith(',')) {
                parts.push(` ${locationObject.country}`); // Add space after comma
            } else {
                parts.push(` ${locationObject.country}`); // Add space
            }
            } else {
            parts.push(locationObject.country); // Only country
            }
        }

        // Join the parts to form the name string
        return parts.join('');
    }
}

export default Location;